import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { authApi, type MeResult } from "../api.js";

interface AuthState {
  user: MeResult | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<{ ok: boolean; error?: string }>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<MeResult | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    const res = await authApi.session();
    setUser(res.ok && res.data?.authenticated && res.data.user ? res.data.user : null);
    setLoading(false);
  };

  useEffect(() => {
    void refresh();
  }, []);

  const login = async (email: string, password: string) => {
    const res = await authApi.login(email, password);
    if (!res.ok) return { ok: false, error: res.error?.errorCode ?? "LOGIN_FAILED" };
    await refresh();
    return { ok: true };
  };

  const logout = async () => {
    await authApi.logout();
    setUser(null);
  };

  return <AuthContext.Provider value={{ user, loading, login, logout, refresh }}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
